import { Table } from 'antd';
import { EditIcon } from 'components/Icons';
import RevxFlexLayout from 'components/RevxFlexLayout';
import RevxTextWithTooltip from 'components/RevxTextWithTooltip';
import { RevxMoney } from 'components/RevxMoney';
import { CostDistributionPayload } from 'domains/orderDomain';

export type OracleBillingCodeRow = {
  key: string;
  oracleString: string;
  percent?: number;
  amount?: number;
};

/**
 * Splits a charge by a percentage, rounded to cents like the backend does.
 */
const amountOfPercent = (percent?: number, totalCharge?: number) => {
  if (percent === undefined || percent === null || totalCharge === undefined) {
    return undefined;
  }
  return Math.round(totalCharge * percent) / 100;
};

/**
 * Flattens a cost distribution into the Oracle Billing Code rows shown on the
 * job adjustment page. A manually entered billing line has no oracle settings,
 * so it is turned into a single row covering the whole job.
 *
 * The amounts are re-derived from the percentages and the charge the adjustment
 * currently stands at, so they follow every price edit made further down.
 */
export const getOracleBillingCodeRows = (
  costDistribution?: CostDistributionPayload,
  totalCharge?: number
): OracleBillingCodeRow[] => {
  const settings = costDistribution?.oracleSettings || [];
  if (settings.length > 0) {
    return settings
      .filter((setting) => setting.oracleString)
      .map((setting, index) => ({
        key: `oracle-${index}`,
        oracleString: setting.oracleString as string,
        percent: setting.percent,
        amount: amountOfPercent(setting.percent, totalCharge) ?? setting.amount,
      }));
  }

  const manualOracleString = costDistribution?.manualOracleString?.trim();
  if (manualOracleString) {
    return [
      {
        key: 'oracle-manual',
        oracleString: manualOracleString,
        percent: 100,
        amount: totalCharge,
      },
    ];
  }

  return [];
};

type Props = {
  rows: OracleBillingCodeRow[];
  canShowEditButton: boolean;
  className?: string;
  onEdit: () => void;
};

const JobAdjustmentOracleBillingCode = function ({
  rows,
  canShowEditButton,
  className = '',
  onEdit,
}: Props) {
  if (rows.length === 0) return null;

  const editIcon = canShowEditButton ? (
    <div className="edit-icon" onClick={onEdit}>
      <EditIcon />
    </div>
  ) : null;

  if (rows.length === 1) {
    return (
      <RevxFlexLayout className={`job-info__oracle ${className}`}>
        <span className="job-info__label">Oracle Billing Code:</span>
        <div className="job-info__oracle-value">
          <RevxTextWithTooltip text={rows[0].oracleString} maxWidth="100%" />
        </div>
        {editIcon}
      </RevxFlexLayout>
    );
  }

  const columns = [
    {
      title: 'Oracle Billing Code',
      dataIndex: 'oracleString',
      key: 'oracleString',
      render: (text: string) => (
        <RevxTextWithTooltip text={text} maxWidth="100%" />
      ),
    },
    {
      title: 'Percent',
      dataIndex: 'percent',
      key: 'percent',
      align: 'right' as const,
      width: 100,
      render: (value?: number) => (value !== undefined ? `${value}%` : '-'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      width: 130,
      render: (value?: number) => <RevxMoney value={value || 0} />,
    },
  ];

  return (
    <div className={`job-info__oracle ${className}`}>
      <RevxFlexLayout>
        <span className="job-info__label">Oracle Billing Code:</span>
        {editIcon}
      </RevxFlexLayout>
      <Table
        className="job-info__oracle-table"
        rowKey="key"
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="small"
        tableLayout="fixed"
        bordered
      />
    </div>
  );
};

export default JobAdjustmentOracleBillingCode;
